import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Chip from '../chip/Chip'
import { ContentStateProperty } from '../../../../@types/types'

export type ChipGroupFeature = {
  translationKey?: string
  property: ContentStateProperty
  value: boolean
  defaultTranslationText: string
  disabled?: boolean
}

export type ChipsGroupProps = {
  id: string
  className?: string
  chipRelatedClassName?: string
  innerContentClassName?: string
  containerClassName?: string
  features: ChipGroupFeature[]
  translation: string
  onChange: (property: ContentStateProperty, value: string | boolean | undefined) => void
  onBlur?: (property: ContentStateProperty, value: boolean, isUnchanged: boolean) => void
}

const ChipsGroup: React.FunctionComponent<ChipsGroupProps> = ({
  id,
  className,
  chipRelatedClassName,
  innerContentClassName,
  containerClassName,
  features,
  translation,
  onChange,
  onBlur,
}: ChipsGroupProps) => {
  const { t } = useTranslation()
  const [state, setState] = useState<{
    isUnchanged: boolean
    previousFeatures: ChipGroupFeature[]
    currentFeatures: ChipGroupFeature[]
  }>({
    isUnchanged: true,
    previousFeatures: features,
    currentFeatures: features,
  })

  const doChange = (property: ContentStateProperty, input: boolean) => {
    onChange(property, input)
    const updatedFeatures = [...state.currentFeatures]
    const updatedIndex = state.currentFeatures.findIndex((feature) => feature.property === property)
    updatedFeatures[updatedIndex] = {
      ...updatedFeatures[updatedIndex],
      value: input,
    }
    setState({
      ...state,
      currentFeatures: updatedFeatures,
    })
  }

  function getChipGroupId(chip: React.FocusEvent<HTMLSpanElement>) {
    return chip.relatedTarget?.parentElement?.parentElement?.parentElement?.parentElement?.id
  }

  function chipIsInTheSameChipGroup(chip: React.FocusEvent<HTMLSpanElement>) {
    return chip?.relatedTarget?.firstChild?.nodeName === 'SPAN' && getChipGroupId(chip) === id
  }

  function findIndexByProperty(
    feature: ChipGroupFeature,
    chipGroupFeatures: ChipGroupFeature[] = state.previousFeatures
  ) {
    return chipGroupFeatures.findIndex((f) => f.property === feature.property)
  }

  function bySamePropertyAndDifferentValue(): (feature: ChipGroupFeature) => boolean {
    return (feature) => feature.value !== state.previousFeatures[findIndexByProperty(feature)].value
  }

  function runOnBlur(): (feature: ChipGroupFeature) => ChipGroupFeature {
    return (newChangedFeature) => {
      onBlur && onBlur(newChangedFeature.property, newChangedFeature.value, state.isUnchanged)
      return newChangedFeature
    }
  }

  const onLostFocus = (chip: React.FocusEvent<HTMLSpanElement>) => {
    if (chipIsInTheSameChipGroup(chip)) {
      return
    }
    const changedFeature = state.currentFeatures.filter(bySamePropertyAndDifferentValue()).map(runOnBlur())
    changedFeature.length && setState({ ...state, previousFeatures: state.currentFeatures, isUnchanged: false })
  }

  const chips: JSX.Element[] = features.map((chip) => (
    <Chip
      key={chip.property}
      active={chip.value}
      disabled={chip.disabled}
      className={`${chipRelatedClassName ?? ''}`}
      innerContentClassName={innerContentClassName}
      containerClassName={containerClassName}
      activeChanged={(input: boolean) => doChange(chip.property, input)}
      onBlur={(obj: React.FocusEvent<HTMLSpanElement>) => {
        onLostFocus(obj)
      }}
    >
      {t(translation + (chip.translationKey ?? chip.property), chip.defaultTranslationText)}
    </Chip>
  ))

  return (
    <div id={id} className={className}>
      <div>
        <div className={`grid-flex gutter-s`}>{chips}</div>
      </div>
    </div>
  )
}

export default ChipsGroup
